import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import styles from "../styles/StickyFooter.module.scss";
import { isFrappeOrJotFormClass } from "../utils/helpers";
import { getOnboardingOrFormCtaProps } from "../utils/applyCtaButtonProps";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../utils/b2c/constants";
import { persistSourceContext } from "../utils/b2c/sourceTracking";

const slugToRegex = (slug: string) => {
  // normalize trailing slash
  const normalized = slug.replace(/\/$/, "");

  // replace "_" segments with regex matcher
  const regexStr = normalized
    .split("/")
    .map((part) => (part === "_" ? "[^/]+" : part))
    .join("/");

  return new RegExp(`^${regexStr}$`);
};

const findMatchingPage = (pages: any[], path: string) => {
  const cleanPath = path.replace(/\/$/, "");

  let bestMatch: any = null;
  let bestDepth = -1;

  for (const page of pages) {
    const pageSlug = page.slug.replace(/\/$/, "");
    const regex = slugToRegex(pageSlug);
    if (regex.test(cleanPath)) {
      const depth = pageSlug.split("/").length;
      if (depth > bestDepth) {
        bestMatch = page;
        bestDepth = depth;
      }
    }
  }

  return bestMatch;
};

const StickyFooter: React.FC = () => {
  const [stickyFooterPages, setStickyFooterPages] = useState<any[]>([]);
  const [showButton, setShowButton] = useState<boolean>(false);
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;
    fetch("/sticky-footer-pages.json")
      .then((res) => (res.ok ? res.json() : { pages: [] }))
      .then((data) => {
        if (!cancelled && Array.isArray(data?.pages)) {
          setStickyFooterPages(data.pages);
        }
      })
      .catch(() => {
        // Silently fall back to empty list; StickyFooter is non-essential.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const pathWithoutQuery = router.asPath.split("?")[0].split("#")[0];

  let stickyFooterPath = stickyFooterPages.filter(
    (page: any) => page.slug === pathWithoutQuery,
  );

  if (stickyFooterPath.length === 0) {
    stickyFooterPath = stickyFooterPages.filter(
      (page: any) => page.slug === pathWithoutQuery.split("/")?.[1],
    );
  }

  // fallback to pattern matching if no match found
  if (stickyFooterPath.length === 0) {
    const matchedPage = findMatchingPage(stickyFooterPages, pathWithoutQuery);
    if (matchedPage) stickyFooterPath = [matchedPage];
  }

  const includeStickyFooter = stickyFooterPath.length > 0;

  const checkElementVisibility = () => {
    const element = document.querySelector(
      `#sticky-enquire-form-viewer`,
    ) as HTMLElement;
    if (!element) {
      setShowButton(true);
      return;
    }

    const elementPosition = element.getBoundingClientRect();
    const eightyPercentHeight = window.innerHeight * 0.8;

    // Only show button when element has scrolled past 80% of viewport height AND is not at the very top
    if (
      elementPosition.top < eightyPercentHeight &&
      elementPosition.top < -eightyPercentHeight
    ) {
      setShowButton(true);
    } else {
      setShowButton(false);
    }
  };

  useEffect(() => {
    checkElementVisibility();
    window.addEventListener("scroll", checkElementVisibility);
    return () => {
      window.removeEventListener("scroll", checkElementVisibility);
    };
  }, []);

  const handleClick =
    (applyCtaButtonProps: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
      if (applyCtaButtonProps.mode === "onboarding") {
        persistSourceContext({
          sourceUrl: window.location.href,
          primarySource: PRIMARY_SOURCE_B2C_WIDGET,
          fifthStepVisible:
            stickyFooterPath?.[0]?.onBoardingFlow?.nearestOffice ?? true,
          coachingVisible:
            stickyFooterPath?.[0]?.onBoardingFlow?.lookingForCoaching ?? true, // CFM-1514
        });
      }
      applyCtaButtonProps.onClick?.(e);
    };

  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: stickyFooterPath?.[0]?.externalLink,
    formId: stickyFooterPath?.[0]?.jotformId,
    toOnboardingEnabled: stickyFooterPath?.[0]?.toOnboardingEnabled,
    sourceUrl: stickyFooterPath?.[0]?.slug === '/' ? 'home' : stickyFooterPath?.[0]?.slug?.replace('/', ''),
  });

  return (
    <>
      {includeStickyFooter && showButton && (
        <>
          <button
            onClick={handleClick(applyCtaButtonProps)}
            className={`d-block d-sm-none btn ${styles.stickyFooter} ${applyCtaButtonProps.formClassName}`}
          >
            {stickyFooterPath[0]?.buttonLabel || "Enquire Now"}
          </button>
        </>
      )}
    </>
  );
};

export default StickyFooter;
